import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CommunityLayout } from '../components/CommunityLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import {
  MapPin,
  Users,
  Plus,
  Bell,
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  Pencil,
  X,
  Building2,
  Check,
  CalendarDays,
} from 'lucide-react';
import {
  connectMeetApi,
  ConnectMeetEvent,
  EventType,
  AUDIENCE_OPTIONS,
} from '../services/connectMeetApi';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

const DUTCH_PROVINCES = [
  'Groningen', 'Friesland', 'Drenthe', 'Overijssel', 'Flevoland',
  'Gelderland', 'Utrecht', 'Noord-Holland', 'Zuid-Holland',
  'Zeeland', 'Noord-Brabant', 'Limburg',
];

const DUTCH_CITIES = [
  'Amsterdam', 'Rotterdam', 'Den Haag', 'Utrecht', 'Eindhoven',
  'Groningen', 'Tilburg', 'Almere', 'Breda', 'Nijmegen',
  'Leiden', 'Maastricht', 'Arnhem', 'Enschede', 'Haarlem',
  'Amersfoort', 'Apeldoorn', 'Dordrecht', 'Delft', 'Zwolle',
  'Deventer', "'s-Hertogenbosch", 'Leeuwarden', 'Middelburg', 'Assen',
];

const EVENT_TYPE_CONFIG: Record<
  EventType,
  { label: string; emoji: string; badgeClass: string; placeholderClass: string }
> = {
  borrel: {
    label: 'Borrel / Vrijmibo',
    emoji: '🍺',
    badgeClass: 'bg-orange-100 text-orange-700',
    placeholderClass: 'bg-orange-100',
  },
  lunch: {
    label: 'Lunch',
    emoji: '🍽️',
    badgeClass: 'bg-green-100 text-green-700',
    placeholderClass: 'bg-green-100',
  },
  wandeling: {
    label: 'Wandeling',
    emoji: '🚶',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    placeholderClass: 'bg-emerald-100',
  },
  grab_a_coffee: {
    label: 'Grab a coffee',
    emoji: '☕',
    badgeClass: 'bg-amber-100 text-amber-700',
    placeholderClass: 'bg-amber-100',
  },
  samen_werken: {
    label: 'Samen werken',
    emoji: '💻',
    badgeClass: 'bg-blue-100 text-blue-700',
    placeholderClass: 'bg-blue-100',
  },
  anders: {
    label: 'Anders',
    emoji: '✨',
    badgeClass: 'bg-gray-100 text-gray-700',
    placeholderClass: 'bg-gray-100',
  },
};

const audienceLabelMap = Object.fromEntries(
  AUDIENCE_OPTIONS.map((o) => [o.value, o.label])
);

// ─── Event Card ──────────────────────────────────────────────────────────────

const EventCard: React.FC<{
  event: ConnectMeetEvent;
  onAttendToggle: (id: string, comment?: string) => void;
  isTogglingAttend: boolean;
  onInviteClick?: (event: ConnectMeetEvent) => void;
}> = ({ event, onAttendToggle, isTogglingAttend, onInviteClick }) => {
  const { t } = useTranslation('pages');
  const [showAttendees, setShowAttendees] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [attendComment, setAttendComment] = useState('');
  const typeConf = EVENT_TYPE_CONFIG[event.type] ?? EVENT_TYPE_CONFIG.anders;

  const formattedDate = (() => {
    try {
      return format(parseISO(event.event_date), 'EEEE d MMMM', { locale: nl });
    } catch {
      return event.event_date;
    }
  })();

  const timeLabel = event.time_to
    ? `${event.time_from} – ${event.time_to}`
    : event.time_from;

  const descriptionExcerpt =
    event.description && event.description.length > 120
      ? event.description.slice(0, 120) + '…'
      : event.description;

  const attendeeCountLabel = event.max_persons
    ? `${event.attendee_count} / ${event.max_persons} ${t('connectMeet.participants')}`
    : `${event.attendee_count} ${event.attendee_count !== 1 ? t('connectMeet.participants') : t('connectMeet.participant')}`;

  // Show regios or fall back to single regio
  const regioLabel = event.regios && event.regios.length > 0
    ? event.regios.join(', ')
    : event.regio;

  const locationLabel = event.city
    ? `${event.location} · ${event.city}`
    : event.location;

  const audienceLabels = event.audience && event.audience.length > 0
    ? event.audience.map((a) => audienceLabelMap[a] ?? a)
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#0B1F3A] hover:shadow-sm transition-all flex flex-col">
      {/* Cover image or colored placeholder */}
      {event.image_url ? (
        <img src={event.image_url} alt="" className="w-full h-36 object-cover" />
      ) : (
        <div className={`w-full h-36 flex items-center justify-center text-5xl ${typeConf.placeholderClass}`}>
          {typeConf.emoji}
        </div>
      )}

      <div className="p-4 flex flex-col flex-1">
        {/* Type badge + edit */}
        <div className="flex items-center justify-between mb-2">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full ${typeConf.badgeClass}`}>
            {typeConf.emoji}{' '}
            {event.type === 'anders' && event.type_custom ? event.type_custom : typeConf.label}
          </span>
          {event.is_creator && (
            <Link
              to={`/connect-meet/${event.id}/bewerken`}
              className="flex items-center gap-1 text-xs text-[#0B1F3A] hover:underline"
            >
              <Pencil className="w-3 h-3" />
              {t('connectMeet.edit')}
            </Link>
          )}
        </div>

        {/* Audience badges */}
        {audienceLabels && (
          <div className="flex flex-wrap gap-1 mb-2">
            {audienceLabels.map((label) => (
              <span key={label} className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                {label}
              </span>
            ))}
          </div>
        )}

        {/* Date & time */}
        <div className="flex items-center gap-1.5 text-sm text-gray-700 mb-1">
          <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="capitalize">{formattedDate}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
          <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          {timeLabel}
        </div>
        {event.extra_dates && event.extra_dates.length > 0 && (
          <div className="flex items-start gap-1.5 text-xs text-gray-400 mb-1">
            <CalendarDays className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              +{event.extra_dates.length} {event.extra_dates.length > 1 ? t('connectMeet.extraDates') : t('connectMeet.extraDate')}
              {': '}
              {event.extra_dates.map((dt) => {
                try {
                  return format(new Date(dt.date), 'd MMM', { locale: nl });
                } catch {
                  return dt.date;
                }
              }).join(', ')}
            </span>
          </div>
        )}

        {/* Location */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
          <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="truncate">{locationLabel}</span>
        </div>

        {/* Regions */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
          <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="truncate">{regioLabel}</span>
        </div>

        {/* Attendees */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
          <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          {attendeeCountLabel}
        </div>

        {/* Description */}
        {descriptionExcerpt && (
          <p className="text-xs text-gray-500 mb-3 leading-relaxed flex-1">
            {descriptionExcerpt}
          </p>
        )}

        {/* Creator */}
        <p className="text-xs text-gray-400 mb-3">{t('connectMeet.by')} {event.creator_name}</p>

        {/* Action button */}
        <div className="mt-auto">
          {event.is_attending ? (
            <button
              onClick={() => onAttendToggle(event.id)}
              disabled={isTogglingAttend}
              className="w-full text-sm font-medium px-3 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              {t('connectMeet.registeredClickToCancel')}
            </button>
          ) : event.is_full ? (
            <button
              disabled
              className="w-full text-sm font-medium px-3 py-2 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed"
            >
              {t('connectMeet.full')}
            </button>
          ) : showCommentInput ? (
            <div className="space-y-2">
              <textarea
                value={attendComment}
                onChange={(e) => setAttendComment(e.target.value)}
                placeholder={t('connectMeet.commentPlaceholder')}
                maxLength={280}
                rows={2}
                className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowCommentInput(false); setAttendComment(''); }}
                  className="flex-1 text-xs px-2 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                >
                  {t('connectMeet.cancel')}
                </button>
                <button
                  onClick={() => {
                    onAttendToggle(event.id, attendComment.trim() || undefined);
                    setShowCommentInput(false);
                    setAttendComment('');
                  }}
                  disabled={isTogglingAttend}
                  className="flex-1 text-xs px-2 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {t('connectMeet.confirmRegistration')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowCommentInput(true)}
              disabled={isTogglingAttend}
              className="w-full text-sm font-medium px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {t('connectMeet.register')}
            </button>
          )}
        </div>

        {/* Invite button for creator */}
        {event.is_creator && onInviteClick && (
          <button
            onClick={() => onInviteClick(event)}
            className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs text-[#0B1F3A] border border-[#0B1F3A]/30 rounded-lg py-1.5 hover:bg-blue-50 transition-colors"
          >
            <Users className="w-3.5 h-3.5" />
            {t('connectMeet.inviteSomeone')}
          </button>
        )}

        {/* Attendee list (collapsible) */}
        {(event.is_attending || event.is_creator) && event.attendees && event.attendees.length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <button
              onClick={() => setShowAttendees((v) => !v)}
              className="flex items-center gap-1 text-xs text-[#0B1F3A] hover:underline"
            >
              {showAttendees ? (
                <><ChevronUp className="w-3 h-3" />{t('connectMeet.hideAttendees')}</>
              ) : (
                <><ChevronDown className="w-3 h-3" />{t('connectMeet.showAttendees', { count: event.attendees.length })}</>
              )}
            </button>
            {showAttendees && (
              <ul className="mt-2 space-y-2">
                {event.attendees.map((a) => {
                  const initials = `${a.first_name[0] ?? ''}${a.last_name[0] ?? ''}`.toUpperCase();
                  return (
                    <li key={a.user_id} className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="w-6 h-6 rounded-full bg-[#0B1F3A] text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0 mt-0.5">
                        {initials}
                      </span>
                      <div>
                        <span className="font-medium">{a.first_name} {a.last_name}</span>
                        {a.comment && (
                          <p className="text-gray-400 italic mt-0.5">"{a.comment}"</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Region Subscription Section ─────────────────────────────────────────────

const RegionSubscriptions: React.FC = () => {
  const [cityInput, setCityInput] = useState('');
  const [showProvinces, setShowProvinces] = useState(false);
  const [showCities, setShowCities] = useState(false);
  const queryClient = useQueryClient();

  const { data: subscriptions = [], isLoading } = useQuery({
    queryKey: ['connect-meet-subscriptions'],
    queryFn: connectMeetApi.getRegionSubscriptions,
  });

  const { t: tCM } = useTranslation('pages');

  const subscribeMutation = useMutation({
    mutationFn: connectMeetApi.subscribeToRegion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connect-meet-subscriptions'] });
      toast.success(tCM('connectMeet.notificationSet'));
    },
    onError: () => toast.error(tCM('connectMeet.somethingWentWrong')),
  });

  const unsubscribeMutation = useMutation({
    mutationFn: connectMeetApi.unsubscribeFromRegion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connect-meet-subscriptions'] });
      toast.success(tCM('connectMeet.notificationDisabled'));
    },
    onError: () => toast.error(tCM('connectMeet.somethingWentWrong')),
  });

  const subscribedProvinces = subscriptions.filter((s) => DUTCH_PROVINCES.includes(s));

  const toggleProvince = (province: string) => {
    if (subscriptions.includes(province)) {
      unsubscribeMutation.mutate(province);
    } else {
      subscribeMutation.mutate(province);
    }
  };

  const addCity = () => {
    const trimmed = cityInput.trim();
    if (!trimmed || subscriptions.includes(trimmed)) return;
    subscribeMutation.mutate(trimmed);
    setCityInput('');
  };

  return (
    <Card>
      <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Bell className="w-4 h-4 text-[#0B1F3A]" />
        {tCM('connectMeet.notificationsPerRegion')}
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        {tCM('connectMeet.notificationsPerRegionDesc')}
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-7 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Active subscriptions */}
          {subscriptions.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 font-medium mb-1.5">{tCM('connectMeet.activeNotifications')}</p>
              <ul className="space-y-1">
                {subscriptions.map((sub) => (
                  <li
                    key={sub}
                    className="flex items-center justify-between text-xs text-gray-700 bg-blue-50 rounded-lg px-2.5 py-1.5"
                  >
                    <span className="flex items-center gap-1.5">
                      {DUTCH_PROVINCES.includes(sub) ? (
                        <Building2 className="w-3 h-3 text-blue-500 flex-shrink-0" />
                      ) : (
                        <MapPin className="w-3 h-3 text-purple-500 flex-shrink-0" />
                      )}
                      {sub}
                    </span>
                    <button
                      onClick={() => unsubscribeMutation.mutate(sub)}
                      disabled={unsubscribeMutation.isPending}
                      className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 ml-2"
                      title={tCM('connectMeet.removeNotification')}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Province toggle */}
          <button
            onClick={() => setShowProvinces((v) => !v)}
            className="w-full flex items-center justify-between text-xs text-[#0B1F3A] hover:underline mb-2"
          >
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {tCM('connectMeet.provinces')} ({subscribedProvinces.length} {tCM('connectMeet.selected')})
            </span>
            <ChevronDown className={`w-3 h-3 transition-transform ${showProvinces ? 'rotate-180' : ''}`} />
          </button>

          {showProvinces && (
            <div className="grid grid-cols-2 gap-1 mb-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
              {DUTCH_PROVINCES.map((p) => {
                const isActive = subscriptions.includes(p);
                return (
                  <button
                    key={p}
                    onClick={() => toggleProvince(p)}
                    disabled={subscribeMutation.isPending || unsubscribeMutation.isPending}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs text-left transition-all disabled:opacity-50 ${
                      isActive
                        ? 'bg-[#0B1F3A] text-white'
                        : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200'
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${
                        isActive ? 'bg-white border-white' : 'border-gray-300'
                      }`}
                    >
                      {isActive && <Check className="w-2.5 h-2.5 text-[#0B1F3A]" />}
                    </span>
                    {p}
                  </button>
                );
              })}
            </div>
          )}

          {/* City checkboxes */}
          <button
            onClick={() => setShowCities((v) => !v)}
            className="w-full flex items-center justify-between text-xs text-[#0B1F3A] hover:underline mb-2"
          >
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {tCM('connectMeet.cities')} ({subscriptions.filter((s) => DUTCH_CITIES.includes(s)).length} {tCM('connectMeet.selected')})
            </span>
            <ChevronDown className={`w-3 h-3 transition-transform ${showCities ? 'rotate-180' : ''}`} />
          </button>

          {showCities && (
            <div className="grid grid-cols-2 gap-1 mb-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
              {DUTCH_CITIES.map((c) => {
                const isActive = subscriptions.includes(c);
                return (
                  <button
                    key={c}
                    onClick={() => isActive ? unsubscribeMutation.mutate(c) : subscribeMutation.mutate(c)}
                    disabled={subscribeMutation.isPending || unsubscribeMutation.isPending}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs text-left transition-all disabled:opacity-50 ${
                      isActive
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200'
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${
                        isActive ? 'bg-white border-white' : 'border-gray-300'
                      }`}
                    >
                      {isActive && <Check className="w-2.5 h-2.5 text-purple-600" />}
                    </span>
                    {c}
                  </button>
                );
              })}
            </div>
          )}

          {/* City subscription – unlisted city */}
          <div>
            <p className="text-xs text-gray-500 font-medium mb-1.5 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {tCM('connectMeet.addOtherCity')}
            </p>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCity(); } }}
                placeholder={tCM('connectMeet.cityPlaceholder')}
                className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
              />
              <button
                onClick={addCity}
                disabled={!cityInput.trim() || subscribeMutation.isPending}
                className="px-3 py-1.5 text-xs bg-[#0B1F3A] text-white rounded-lg hover:bg-[#1A3459] transition-colors disabled:opacity-50"
              >
                +
              </button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const EventSkeleton: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden animate-pulse">
    <div className="w-full h-36 bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-gray-200 rounded w-1/3" />
      <div className="h-3 bg-gray-100 rounded w-2/3" />
      <div className="h-3 bg-gray-100 rounded w-1/2" />
      <div className="h-8 bg-gray-100 rounded w-full mt-4" />
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export const ConnectMeetPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('pages');

  const [filterRegio, setFilterRegio] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [showMyEvents, setShowMyEvents] = useState(false);

  // Invite modal state
  const [inviteEvent, setInviteEvent] = useState<ConnectMeetEvent | null>(null);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteSearchActive, setInviteSearchActive] = useState('');
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());

  const { data: allEvents = [], isLoading } = useQuery({
    queryKey: ['connect-meet-events', filterRegio, filterType, filterCity],
    queryFn: () =>
      connectMeetApi.getEvents({
        regio: filterRegio || undefined,
        type: filterType || undefined,
        city: filterCity || undefined,
      }),
  });

  const { data: myEvents = [], isLoading: myEventsLoading } = useQuery({
    queryKey: ['connect-meet-my-events'],
    queryFn: connectMeetApi.getMyEvents,
    enabled: showMyEvents,
  });

  const attendMutation = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      connectMeetApi.toggleAttend(id, comment),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['connect-meet-events'] });
      queryClient.invalidateQueries({ queryKey: ['connect-meet-my-events'] });
      if (result.attending) {
        toast.success(t('connectMeet.registeredForEvent'));
      } else {
        toast.success(t('connectMeet.unregistrationProcessed'));
      }
    },
    onError: () => toast.error(t('connectMeet.somethingWentWrong')),
  });

  const inviteMutation = useMutation({
    mutationFn: ({ eventId, userId }: { eventId: string; userId: string }) =>
      connectMeetApi.inviteProfessional(eventId, userId),
    onSuccess: (_data, vars) => {
      setInvitedIds((prev) => new Set([...prev, vars.userId]));
      toast.success(t('connectMeet.invitationSent'));
    },
    onError: () => toast.error(t('connectMeet.somethingWentWrong')),
  });

  const { data: searchResults = [] } = useQuery({
    queryKey: ['connect-meet-professionals', inviteSearchActive],
    queryFn: () => connectMeetApi.searchProfessionals(inviteSearchActive),
    enabled: inviteSearchActive.length >= 2,
  });

  const displayedEvents = showMyEvents ? myEvents : allEvents;
  const loading = showMyEvents ? myEventsLoading : isLoading;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = displayedEvents.filter((e) => {
    try { return new Date(e.event_date) >= today; } catch { return true; }
  });
  const pastEvents = displayedEvents.filter((e) => {
    try { return new Date(e.event_date) < today; } catch { return false; }
  });

  const hasFilters = !!(filterRegio || filterType || filterCity) && !showMyEvents;

  return (
    <CommunityLayout>
    <div className="py-8 ml-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-end">
          <Button onClick={() => navigate('/connect-meet/nieuw')}>
            <Plus className="w-4 h-4 mr-2" />
            {t('connectMeet.createNewEvent')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main content */}
        <div className="lg:col-span-3 space-y-5">
          {/* Filter bar */}
          <Card>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Regio filter */}
                <select
                  value={filterRegio}
                  onChange={(e) => { setFilterRegio(e.target.value); setShowMyEvents(false); }}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                >
                  <option value="">{t('connectMeet.allRegions')}</option>
                  {DUTCH_PROVINCES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>

                {/* Type filter */}
                <select
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value); setShowMyEvents(false); }}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                >
                  <option value="">{t('connectMeet.allTypes')}</option>
                  {(Object.entries(EVENT_TYPE_CONFIG) as [EventType, (typeof EVENT_TYPE_CONFIG)[EventType]][]).map(
                    ([key, conf]) => (
                      <option key={key} value={key}>
                        {conf.emoji} {conf.label}
                      </option>
                    )
                  )}
                </select>

                {/* My events toggle */}
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setShowMyEvents(false)}
                    className={`flex-1 sm:flex-none text-sm px-4 py-2 rounded-lg transition-colors ${
                      !showMyEvents ? 'bg-[#0B1F3A] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t('connectMeet.allEvents')}
                  </button>
                  <button
                    onClick={() => setShowMyEvents(true)}
                    className={`flex-1 sm:flex-none text-sm px-4 py-2 rounded-lg transition-colors ${
                      showMyEvents ? 'bg-[#0B1F3A] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t('connectMeet.myEvents')}
                  </button>
                </div>
              </div>

              {/* City filter */}
              {!showMyEvents && (
                <input
                  type="text"
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                  placeholder={t('connectMeet.searchByCity')}
                  className="w-full sm:w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                />
              )}
            </div>

            {/* Active filter tags */}
            {hasFilters && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span className="text-xs text-gray-500">{t('connectMeet.filteredBy')}</span>
                {filterRegio && (
                  <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                    {filterRegio}
                    <button onClick={() => setFilterRegio('')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filterType && EVENT_TYPE_CONFIG[filterType as EventType] && (
                  <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                    {EVENT_TYPE_CONFIG[filterType as EventType].label}
                    <button onClick={() => setFilterType('')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filterCity && (
                  <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                    {filterCity}
                    <button onClick={() => setFilterCity('')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
            )}
          </Card>

          {/* Event grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => <EventSkeleton key={i} />)}
            </div>
          ) : displayedEvents.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">
                  {showMyEvents
                    ? t('connectMeet.noMyEvents')
                    : t('connectMeet.noEventsFound')}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {showMyEvents
                    ? t('connectMeet.noMyEventsHint')
                    : t('connectMeet.noEventsFoundHint')}
                </p>
                <Button className="mt-5" onClick={() => navigate('/connect-meet/nieuw')}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('connectMeet.createNewEvent')}
                </Button>
              </div>
            </Card>
          ) : (
            <>
              <p className="text-sm text-gray-500">
                {displayedEvents.length} {displayedEvents.length === 1 ? t('connectMeet.eventSingular') : t('connectMeet.eventPlural')} {t('connectMeet.found')}
              </p>
              {upcomingEvents.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {upcomingEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onAttendToggle={(id, comment) => attendMutation.mutate({ id, comment })}
                      isTogglingAttend={attendMutation.isPending}
                      onInviteClick={(ev) => { setInviteEvent(ev); setInvitedIds(new Set()); setInviteSearch(''); setInviteSearchActive(''); }}
                    />
                  ))}
                </div>
              )}
              {pastEvents.length > 0 && (
                <>
                  <p className="text-sm text-gray-400 mt-4">{t('connectMeet.pastEvents')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 opacity-50 grayscale">
                    {pastEvents.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        onAttendToggle={(id, comment) => attendMutation.mutate({ id, comment })}
                        isTogglingAttend={attendMutation.isPending}
                        onInviteClick={(ev) => { setInviteEvent(ev); setInvitedIds(new Set()); setInviteSearch(''); setInviteSearchActive(''); }}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Invite modal */}
        {inviteEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setInviteEvent(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">{t('connectMeet.sendInvitation')}</h2>
                <button onClick={() => setInviteEvent(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                {t('connectMeet.inviteSearchDesc')} <span className="font-medium text-gray-800">{inviteEvent.description ?? t('connectMeet.thisEvent')}</span>.
              </p>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={inviteSearch}
                  onChange={(e) => setInviteSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setInviteSearchActive(inviteSearch); }}
                  placeholder={t('connectMeet.searchByName')}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                />
                <button
                  onClick={() => setInviteSearchActive(inviteSearch)}
                  className="px-3 py-2 bg-[#0B1F3A] text-white rounded-lg text-sm hover:bg-[#1A3459]"
                >
                  {t('connectMeet.search')}
                </button>
              </div>
              {searchResults.length > 0 && (
                <ul className="space-y-2 max-h-56 overflow-y-auto">
                  {searchResults.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 text-sm border border-gray-100 rounded-lg px-3 py-2">
                      <span className="text-gray-800">{p.first_name} {p.last_name}{p.regio ? ` · ${p.regio}` : ''}</span>
                      <button
                        onClick={() => inviteMutation.mutate({ eventId: inviteEvent.id, userId: p.id })}
                        disabled={invitedIds.has(p.id) || inviteMutation.isPending}
                        className={`text-xs px-3 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                          invitedIds.has(p.id)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-[#0B1F3A] text-white hover:bg-[#1A3459]'
                        }`}
                      >
                        {invitedIds.has(p.id) ? t('connectMeet.invited') : t('connectMeet.invite')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {inviteSearchActive.length >= 2 && searchResults.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">{t('connectMeet.noProfessionalsFound')}</p>
              )}
            </div>
          </div>
        )}

        {/* Sidebar */}
        <div className="space-y-4">
          <RegionSubscriptions />

          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">{t('connectMeet.tips')}</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('connectMeet.tip1')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('connectMeet.tip2')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('connectMeet.tip3')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('connectMeet.tip4')}
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
    </CommunityLayout>
  );
};
